class CartDrawer {
  constructor() {
    // Prevent double initialization
    if (window.cartDrawerInstance) {
      return window.cartDrawerInstance;
    }

    this.drawer = document.querySelector("[data-cart-drawer]");
    this.overlay = document.querySelector("[data-cart-drawer-overlay]");
    this.closeButtons = document.querySelectorAll("[data-cart-drawer-close]");
    this.itemsList = document.querySelector("[data-cart-drawer-list]");
    this.emptyState = document.querySelector("[data-cart-drawer-empty]");
    this.subtotalElement = document.querySelector("[data-cart-subtotal]");
    this.checkoutButton = document.querySelector("[data-cart-checkout]");
    this.itemTemplate = document.getElementById("cart-item-template");

    // Check if all required elements exist
    if (!this.drawer) {
      console.error("Cart drawer element not found");
      return;
    }
    if (!this.itemsList) {
      console.error("Cart items list element not found");
      return;
    }
    if (!this.itemTemplate) {
      console.error("Cart item template not found");
      return;
    }

    this.isOpen = false;
    this.isLoading = false;
    this.cartData = null; // Cache cart data

    // Set as singleton instance
    window.cartDrawerInstance = this;

    // Prevent flash on page load by ensuring proper initial state
    this.setInitialState();
    this.init();
  }

  setInitialState() {
    // Prevent flash on page load by ensuring proper initial state
    if (!this.drawer) return;

    // Add no-transition class to prevent animations during initialization
    this.drawer.classList.add("cart-drawer-no-transition");

    // Use inline styles to ensure it's fully hidden
    this.drawer.style.visibility = "hidden";
    this.drawer.style.opacity = "0";

    // Ensure drawer starts closed
    this.drawer.classList.remove("is-open");
    this.isOpen = false;

    // Use double requestAnimationFrame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Remove inline styles first
        this.drawer.style.removeProperty("visibility");
        this.drawer.style.removeProperty("opacity");
        // Then remove the no-transition class
        this.drawer.classList.remove("cart-drawer-no-transition");
      });
    });
  }

  init() {
    this.bindEvents();
    this.updateCartCountOnly(); // Only update cart count on page load
    this.bindCartIconClick();
    this.bindAddToCartForms();
  }

  bindEvents() {
    // Close drawer events
    this.closeButtons.forEach((button) => {
      button.addEventListener("click", () => this.close());
    });

    this.overlay.addEventListener("click", () => this.close());

    // Escape key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Checkout button
    this.checkoutButton.addEventListener("click", () => {
      window.location.href = "/checkout";
    });

    // Prevent body scroll when drawer is open
    this.drawer.addEventListener("transitionend", () => {
      if (this.isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    });
  }

  bindCartIconClick() {
    // Find and update cart icon in header
    const cartIcons = document.querySelectorAll("[data-cart-icon]");

    if (cartIcons.length > 0) {
      cartIcons.forEach((cartIcon) => {
        cartIcon.addEventListener("click", (e) => {
          e.preventDefault();
          this.openDrawer();
        });
      });
    }
  }

  bindAddToCartForms() {
    // Bind to all product forms
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    productForms.forEach((form) => {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.handleAddToCart(form);
      });
    });

    // Bind to quick add buttons if they exist
    const quickAddButtons = document.querySelectorAll("[data-quick-add]");
    quickAddButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const form = button.closest("form") || button.querySelector("form");
        if (form) {
          this.handleAddToCart(form);
        }
      });
    });
  }

  async handleAddToCart(form) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      const formData = new FormData(form);
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        await this.updateCartDisplay();
        await this.refreshRecommendations();
        this.open();
        this.showSuccessMessage();
      } else {
        const error = await response.json();
        this.showErrorMessage(error.message || "Unable to add item to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      this.showErrorMessage("Unable to add item to cart");
    } finally {
      this.setLoading(false);
    }
  }

  // Lightweight method to only update cart count on page load
  async updateCartCountOnly() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) return;

      const cart = await response.json();
      this.updateCartCount(cart.item_count);
    } catch (error) {
      // Silently fail for cart count update
    }
  }

  // Full cart display update (only when drawer is opened)
  async updateCartDisplay() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      this.cartData = cart; // Cache the data

      this.renderCartItems(cart);
      this.updateSubtotal(cart.total_price);
      this.updateCartCount(cart.item_count);

      // Dispatch cart updated event for free shipping bar
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: cart },
        })
      );
    } catch (error) {
      console.error("Error updating cart display:", error);
    }
  }

  // Alias for updateCartDisplay for compatibility
  async fetchCart() {
    return await this.updateCartDisplay();
  }

  // Refresh recommended products section by re-rendering it
  async refreshRecommendations() {
    try {
      // Fetch the cart drawer section HTML with updated cart state
      const response = await fetch(window.location.href);
      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Get the updated recommended section from fetched HTML
      const updatedRecommended = doc.querySelector("[data-cart-recommended]");

      // Get the current recommended section (may not exist)
      const currentRecommended = document.querySelector(
        "[data-cart-recommended]"
      );

      // Find the cart drawer items container to insert after
      const cartDrawerItems = document.querySelector(".cart-drawer-items");

      if (updatedRecommended && currentRecommended) {
        // Section exists and should be updated - replace it
        currentRecommended.replaceWith(updatedRecommended);
      } else if (updatedRecommended && !currentRecommended && cartDrawerItems) {
        // Section should exist but doesn't - insert it after cart items
        cartDrawerItems.insertAdjacentElement("afterend", updatedRecommended);
      } else if (!updatedRecommended && currentRecommended) {
        // Section exists but shouldn't - remove it
        currentRecommended.remove();
      }
      // If neither exists, do nothing
    } catch (error) {
      console.error("Error refreshing recommendations:", error);
    }
  }

  // Open drawer with lazy loading
  async openDrawer() {
    if (!this.cartData) {
      await this.updateCartDisplay();
    }
    this.open();
  }

  renderCartItems(cart) {
    if (!cart.items || cart.items.length === 0) {
      this.showEmptyState();
      return;
    }

    this.hideEmptyState();
    this.itemsList.innerHTML = "";

    cart.items.forEach((item) => {
      const itemElement = this.createCartItemElement(item);
      this.itemsList.appendChild(itemElement);
    });
  }

  createCartItemElement(item) {
    const template = this.itemTemplate.content.cloneNode(true);
    const itemElement = template.querySelector("[data-cart-item]");

    // Set item data
    itemElement.dataset.key = item.key;
    itemElement.dataset.id = item.id;

    // Image
    const image = template.querySelector("[data-cart-item-image]");
    const imageContainer = image.closest(".cart-item-image");

    if (item.image) {
      image.src = item.image;
      image.alt = item.product_title;
      image.width = 80;
      image.height = 80;

      // Make image clickable - link to product page
      if (imageContainer && item.url) {
        imageContainer.style.cursor = "pointer";
        imageContainer.addEventListener("click", () => {
          window.location.href = item.url;
        });
      }
    } else {
      image.style.display = "none";
    }

    // Title
    const title = template.querySelector("[data-cart-item-title]");
    title.textContent = item.product_title;

    // Variant
    const variant = template.querySelector("[data-cart-item-variant]");
    if (item.variant_title && item.variant_title !== "Default Title") {
      variant.textContent = item.variant_title;
    } else {
      variant.style.display = "none";
    }

    // Price
    const price = template.querySelector("[data-cart-item-price]");

    // Check if item is on sale
    if (item.original_line_price > item.final_line_price) {
      // Item is on sale
      price.classList.add("on-sale");
      price.innerHTML = `<span class="original-price">${this.formatMoney(
        item.original_line_price
      )}</span>${this.formatMoney(item.final_line_price)}`;
    } else {
      // Regular price
      price.textContent = this.formatMoney(item.final_line_price);
    }

    // Quantity
    const quantityInput = template.querySelector("[data-cart-item-quantity]");
    quantityInput.value = item.quantity;

    // Bind quantity controls
    const minusBtn = template.querySelector("[data-cart-item-minus]");
    const plusBtn = template.querySelector("[data-cart-item-plus]");
    const removeBtn = template.querySelector("[data-cart-item-remove]");

    minusBtn.addEventListener("click", () =>
      this.updateQuantity(item.key, item.quantity - 1)
    );
    plusBtn.addEventListener("click", () =>
      this.updateQuantity(item.key, item.quantity + 1)
    );
    removeBtn.addEventListener("click", () => this.removeItem(item.key));

    return itemElement;
  }

  async updateQuantity(key, quantity) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
        }),
      });

      if (response.ok) {
        await this.updateCartDisplay();
      } else {
        const error = await response.json();
        this.showErrorMessage(error.message || "Unable to update quantity");
      }
    } catch (error) {
      console.error("Update quantity error:", error);
      this.showErrorMessage("Unable to update quantity");
    } finally {
      this.setLoading(false);
    }
  }

  async removeItem(key) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });

      if (response.ok) {
        await this.updateCartDisplay();
      } else {
        const error = await response.json();
        this.showErrorMessage(error.message || "Unable to remove item");
      }
    } catch (error) {
      console.error("Remove item error:", error);
      this.showErrorMessage("Unable to remove item");
    } finally {
      this.setLoading(false);
    }
  }

  updateSubtotal(totalPrice) {
    this.subtotalElement.textContent = this.formatMoney(totalPrice);
  }

  updateCartCount(itemCount) {
    // Update cart count in header if element exists
    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    cartCountElements.forEach((element) => {
      element.textContent = itemCount;
      element.style.display = itemCount > 0 ? "flex" : "none";
    });
  }

  showEmptyState() {
    this.emptyState.style.display = "flex";
    this.itemsList.style.display = "none";
  }

  hideEmptyState() {
    this.emptyState.style.display = "none";
    this.itemsList.style.display = "block";
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add("is-open");
    document.body.classList.add("cart-drawer-open");

    // Focus management for accessibility
    setTimeout(() => {
      const closeButton = this.drawer.querySelector("[data-cart-drawer-close]");
      if (closeButton) closeButton.focus();
    }, 300);
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Add a closing class to trigger the animation
    this.drawer.classList.add("is-closing");

    // Wait for the animation to complete
    const handleTransitionEnd = async (e) => {
      // Only proceed if this is the container's transform transition
      if (
        e.target.classList.contains("cart-drawer-container") &&
        e.propertyName === "transform"
      ) {
        // Remove both classes
        this.drawer.classList.remove("is-open");
        this.drawer.classList.remove("is-closing");
        document.body.classList.remove("cart-drawer-open");

        // Remove the event listener
        this.drawer.removeEventListener("transitionend", handleTransitionEnd);

        // Refresh recommendations after cart is fully closed
        await this.refreshRecommendations();
      }
    };

    // Add the event listener
    this.drawer.addEventListener("transitionend", handleTransitionEnd);
  }

  setLoading(loading) {
    this.isLoading = loading;
    if (loading) {
      this.drawer.classList.add("cart-drawer-loading");
    } else {
      this.drawer.classList.remove("cart-drawer-loading");
    }
  }

  showSuccessMessage() {
    // You can implement a toast notification here
    console.log("Item added to cart successfully");
  }

  showErrorMessage(message) {
    // You can implement a toast notification here
    console.error("Cart error:", message);
    alert(message); // Temporary fallback
  }

  formatMoney(cents) {
    // Basic money formatting - you may want to use Shopify's money format
    const amount = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }
}

// Make CartDrawer available globally but don't auto-initialize
window.CartDrawer = CartDrawer;

// Export for potential external use
if (typeof module !== "undefined" && module.exports) {
  module.exports = CartDrawer;
}
